import {
  Alert,
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  CardActionArea,
} from '@mui/material'
import API from '../../../../services/API'
import moment from 'moment'
import { TenantSettingsContext, UserContext } from '../../../../App'
import React, { useContext, useEffect, useState } from 'react'
import EmployeeUsage from './EmployeeUsage'
import UsageAllowances from './UsageAllowances'
import ReferralProgramme from './ReferralProgramme'
import ErrorModal from '../../../../components/Modals/Error'
import { ConnectWithoutContact } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import Loading from '../../../loading/loadingPage'

const BillingPage = () => {
  const { user } = useContext(UserContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const [subscriptionInfo, setSubscriptionInfo] = useState({})
  const [availablePlans, setAvailablePlans] = useState([])
  const [interval, setInterval] = useState('monthly') // 'monthly' or 'annually'
  const [loading, setLoading] = useState(true)
  const [contactSupport, setContactSupport] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    API.get('/subscription-info')
      .then((res) => {
        if (res.status === 200) {
          const info = res.data
          setSubscriptionInfo(info)

          // Automatically set interval if possible
          if (info.stripeLookupKey?.includes('_annually')) {
            setInterval('annually')
          } else if (info.stripeLookupKey?.includes('_monthly')) {
            setInterval('monthly')
          }
        }
      })
      .finally(() => setLoading(false))

    API.get('/billing/plans').then((res) => {
      if (res.status === 200 && res.data?.data) {
        setAvailablePlans(res.data.data)
      }
    })
  }, [])

  const {
    subscriptionTier,
    stripeSubscriptionExpiry,
    inInitialTrialPeriod,
    trialEnd,
    subscriptionCancellationDate,
    stripeSubscriptionStatus,
    stripeSubscriptionId,
  } = subscriptionInfo

  const tierLabels = ['Core', 'Premium', 'Enterprise']
  const tierKeys = ['core', 'premium', 'enterprise']
  // Enterprise+ invoiced outside Stripe: access never expires automatically
  // and plan changes are agreed with AmbuNet, so no self-serve purchasing.
  const manualBilling = subscriptionInfo?.manualBilling === true
  const isQuotedPlan =
    subscriptionInfo?.stripeLookupKey?.startsWith('enterprise_pts_') ||
    (!!subscriptionInfo?.contract?.packageName &&
      !['Core', 'Premium', 'Enterprise'].includes(
        subscriptionInfo.contract.packageName,
      ))
  const currentTierLabel =
    (isQuotedPlan || manualBilling) && subscriptionInfo?.contract?.packageName
      ? subscriptionInfo.contract.packageName
      : tierLabels[subscriptionTier - 1]

  const now = moment()

  const trialActive =
    inInitialTrialPeriod && trialEnd && moment(trialEnd).isAfter(now)

  const subscriptionPaid =
    ['active', 'trialing'].includes(stripeSubscriptionStatus) &&
    !!stripeSubscriptionId &&
    stripeSubscriptionExpiry &&
    moment(stripeSubscriptionExpiry).isAfter(now)

  // Server-computed so the page matches the paywall exactly
  const inPaymentGrace =
    !!subscriptionInfo.effectiveGraceUntil &&
    moment(subscriptionInfo.effectiveGraceUntil).isAfter(now)

  const hasEntitlement =
    trialActive || subscriptionPaid || inPaymentGrace || manualBilling

  const hasSubscriptionRecord = !!stripeSubscriptionId
  const isInactive = !hasEntitlement

  const planDescriptions = {
    core: 'For small teams starting out.',
    premium: 'Ideal for growing organisations.',
    enterprise: 'For large and complex operations.',
  }
  const isCurrent = (key, interval) => {
    if (!hasEntitlement) return false
    return subscriptionInfo?.stripeLookupKey === `${key}_${interval}`
  }

  const isDowngrade = (keyIndex) => subscriptionTier > keyIndex + 1
  const hasLiveSubscription =
    ['active', 'trialing', 'past_due', 'unpaid'].includes(
      stripeSubscriptionStatus,
    ) && !!stripeSubscriptionId

  const isSameTier = (keyIndex) => subscriptionTier === keyIndex + 1
  const getCurrentInterval = () => {
    if (subscriptionInfo?.stripeLookupKey?.includes('_annually'))
      return 'annually'
    if (subscriptionInfo?.stripeLookupKey?.includes('_monthly'))
      return 'monthly'
    return null
  }

  const handleUpgrade = async (lookupKey) => {
    setLoading(true)
    const res = await API.post('/billing/create-checkout-session', {
      lookupKey,
    })
    if (res.data?.url) {
      window.location.href = res.data.url
    } else {
      setLoading(false)
      alert('Unable to open checkout. Please contact support.')
    }
  }

  const handleIntervalChange = (_, newInterval) => {
    if (newInterval) setInterval(newInterval)
  }

  const getCustomerPortal = async () => {
    await API.post('/stripe/customer-portal').then((res) => {
      if (res.status === 200 && res.data?.url) {
        window.location.href = res.data.url
      } else {
        console.error('Could not retrieve Customer portal URL')
      }
    })
  }

  return loading ? (
    <Box width="100%" height="100%" alignItems="center" justifyContent="center">
      <Loading />
    </Box>
  ) : (
    <Box maxWidth="lg" mx="auto" p={4}>
      <Typography variant="h4" gutterBottom>
        Billing & Subscription
      </Typography>

      <Stack
        component={Paper}
        sx={{ p: 3, mb: 4, border: 'solid 1px' }}
        gap={1}
      >
        <Typography variant="h6" color="contrast.main">
          {hasEntitlement ? 'Current Plan' : 'Subscription Status'}
        </Typography>
        <Typography
          variant="h4"
          gutterBottom
          color={
            inInitialTrialPeriod && moment(trialEnd).isAfter(moment())
              ? 'warning.main'
              : isInactive
                ? 'error.main'
                : 'success.main'
          }
        >
          {inInitialTrialPeriod && moment(trialEnd).isAfter(moment())
            ? 'Free Trial'
            : isInactive
              ? stripeSubscriptionStatus === 'past_due'
                ? 'Payment Past Due'
                : 'No Active Subscription'
              : currentTierLabel || 'Unknown'}
        </Typography>
        {/* {inInitialTrialPeriod && trialEnd && (
          <Typography variant="body2" color="text.secondary">
            {moment(trialEnd).isBefore(moment())
              ? 'Your Trial has Ended'
              : `Trial ends on ${moment(trialEnd).format('DD MMM YYYY')}`}
          </Typography>
        )} */}
        {manualBilling ? (
          <Typography variant="body2" color="text.secondary">
            Billed by invoice — your subscription and renewals are managed by
            AmbuNet.
          </Typography>
        ) : inInitialTrialPeriod && trialEnd ? (
          <Typography variant="body2" color="text.secondary">
            {moment(trialEnd).isBefore(moment())
              ? 'Your trial has ended'
              : `Trial ends on ${moment(trialEnd).format('DD MMM YYYY')}`}
          </Typography>
        ) : subscriptionPaid &&
          stripeSubscriptionExpiry &&
          !subscriptionCancellationDate ? (
          <Typography variant="body2" color="text.secondary">
            Your subscription will renew automatically on{' '}
            {moment(stripeSubscriptionExpiry).format('DD MMM YYYY')}.
          </Typography>
        ) : stripeSubscriptionStatus === 'canceled' &&
          stripeSubscriptionExpiry ? (
          <Typography variant="body2" color="text.secondary">
            Subscription expired on{' '}
            {moment(stripeSubscriptionExpiry).format('DD MMM YYYY')}
          </Typography>
        ) : stripeSubscriptionExpiry &&
          moment(stripeSubscriptionExpiry).isSameOrBefore(moment()) ? (
          <Typography variant="body2" color="text.secondary">
            Subscription expired on{' '}
            {moment(stripeSubscriptionExpiry).format('DD MMM YYYY')}
          </Typography>
        ) : null}
        {hasLiveSubscription &&
          !(inInitialTrialPeriod && !stripeSubscriptionExpiry) && (
            <Chip
              label="Manage Subscription"
              color="contrast"
              sx={{ borderRadius: 1 }}
              onClick={() => getCustomerPortal()}
            />
          )}
      </Stack>
      {/* Usage against the plan's allowances. It gates itself to the tenant
          owner and site administrators, because /billing is open to any
          signed-in user. */}
      <UsageAllowances />
      {hasLiveSubscription && <EmployeeUsage />}
      {manualBilling ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Your organisation is billed by invoice under an agreed AmbuNet
          package. Plan changes and renewals are managed by AmbuNet — contact
          your account manager or billing@ambunet.co.uk with any questions.
        </Alert>
      ) : (
        isQuotedPlan && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Your organisation is on a quoted AmbuNet package. Plan changes are
            managed by AmbuNet, but billing documents and payment details
            remain available through Stripe.
          </Alert>
        )
      )}
      {/* TODO: Invoices */}
      {!isQuotedPlan && !manualBilling && (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Upgrade</Typography>
            <ToggleButtonGroup
              value={interval}
              exclusive
              onChange={handleIntervalChange}
              size="small"
            >
              <ToggleButton value="monthly">Monthly</ToggleButton>
              <ToggleButton value="annually">Annually</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Grid container spacing={3}>
            {tierKeys.map((key, index) => {
              const isCurrentPlan = isCurrent(key, interval)

              const tierData = availablePlans.find(
                (p) =>
                  p.lookup_key === `${key}_${interval}` &&
                  p.recurring?.interval ===
                    (interval === 'annually' ? 'year' : 'month'),
              )

              const priceString = tierData
                ? `${orgSettings?.currency ?? '£'}${(tierData.unit_amount / 100).toFixed(0)} / ${
                    interval === 'annually' ? 'year' : 'month'
                  }`
                : 'Unavailable'

              return (
                <Grid item xs={12} md={4} key={key}>
                  <Card
                    sx={{
                      border: isCurrentPlan
                        ? '2px solid #1976d2'
                        : '1px solid #ccc',
                    }}
                    elevation={isCurrentPlan ? 6 : 1}
                  >
                    <CardContent>
                      <Typography variant="h6">{tierLabels[index]}</Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, minHeight: 48 }}
                        color="text.secondary"
                      >
                        {planDescriptions[key]}
                      </Typography>
                      <Typography variant="h6" sx={{ mt: 1 }}>
                        {priceString}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        variant="contained"
                        fullWidth
                        color={isCurrentPlan ? 'contrast' : 'secondary'}
                        onClick={() => {
                          if (isCurrentPlan) return
                          if (hasLiveSubscription && isDowngrade(index)) {
                            setContactSupport(true)
                            return
                          }
                          handleUpgrade(`${key}_${interval}`)
                        }}
                      >
                        {isCurrentPlan
                          ? 'Current Plan'
                          : !hasLiveSubscription
                            ? 'Purchase'
                            : isSameTier(index) &&
                                getCurrentInterval() !== interval
                              ? `Switch to ${
                                  interval === 'monthly'
                                    ? 'Monthly'
                                    : 'Annually'
                                }`
                              : isDowngrade(index)
                                ? 'Downgrade'
                                : 'Upgrade'}
                      </Button>
                      <ErrorModal
                        open={contactSupport}
                        onClose={() => setContactSupport(false)}
                        icon={ConnectWithoutContact}
                        title="Downgrade Subscription"
                        message="Please contact Support to downgrade your subscription"
                        button={
                          <Button onClick={() => navigate('/support')}>
                            Contact Support
                          </Button>
                        }
                        noAutoClose
                      />
                    </CardActions>
                  </Card>
                </Grid>
              )
            })}
            <Grid item xs={12}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="100%"
              >
                <Button
                  variant="contained"
                  onClick={() => window.open('https://ambunet.co.uk/pricing')}
                >
                  View Features
                </Button>
              </Box>
            </Grid>
          </Grid>
        </>
      )}
      {isQuotedPlan && (
        <Box display="flex" justifyContent="center">
          <Button
            variant="contained"
            onClick={() => window.open('https://ambunet.co.uk/pricing')}
          >
            View Features
          </Button>
        </Box>
      )}
      {/* Referral programme lives here because its reward is billing account
          credit; it used to sit on the Support page. */}
      <Box mt={4}>
        <ReferralProgramme />
      </Box>
    </Box>
  )
}

export default BillingPage
